import {
  extraDesktopWidthMax,
  mediumDesktopExactWidth,
  tabletWidth,
  smallDesktopWidth,
  mobileWidthLarge,
  middleMobileWidth,
  smallMobileWidth,
  mobileWidthMax,
  extraDesktopWidth,
  largeDesktopWidth,
  themeColor,
  accessibilityColors,
} from '@edulastic/colors'
import { Affix } from 'antd'
import { PropTypes } from 'prop-types'
import React, { useState, useRef, useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import { compose } from 'redux'
import styled, { css } from 'styled-components'
import { IconPencilEdit, IconPlus, IconMinusRounded } from '@edulastic/icons'
import { TextInputOnFocusStyled } from './InputStyles'
import { MenuIcon } from './MenuIcon'
import { toggleSideBarAction } from '../../../../src/client/author/src/actions/toggleMenu'

const MainHeader = ({
  children,
  headingText,
  titleText,
  Icon,
  toggleSideBar,
  titleMarginTop,
  containerClassName,
  headerLeftClassName,
  hasTestId,
  isInModal,
  isEditable = false,
  onTitleChange,
  height,
  ...restProps
}) => {
  const title = titleText || headingText
  const [showPencil, setShowPencil] = useState(true)
  const [isTitleExpanded, setIsTitleExpanded] = useState(false)
  const [showExpandButton, setShowExpandButton] = useState(false)
  const [headerHeight, setHeaderHeight] = useState(0)
  const [originalMarginTop, setOriginalMarginTop] = useState(null)
  const headerRef = useRef(null)
  const titleRef = useRef(null)

  const measureHeaderHeight = useCallback(() => {
    if (headerRef.current) {
      const actualHeight = headerRef.current.getBoundingClientRect().height
      setHeaderHeight(actualHeight)
    }
  }, [headerRef])

  const updateMainContainerMargin = useCallback(() => {
    const mainContainer = document.getElementById('main')
    if (mainContainer && headerRef.current) {
      const currentHeaderHeight = headerRef.current.getBoundingClientRect()
        .height
      const baseHeaderHeight = headerHeight || 60 // Fallback to default header height
      const additionalHeight = currentHeaderHeight - baseHeaderHeight

      // Store original margin on first run
      if (originalMarginTop === null) {
        const computedStyle = window.getComputedStyle(mainContainer)
        const currentMarginTop = parseFloat(computedStyle.marginTop) || 0
        setOriginalMarginTop(currentMarginTop)
      }

      // Calculate new margin-top based on expansion state
      let newMarginTop
      if (isTitleExpanded && additionalHeight > 0) {
        // Expand: original margin + additional height
        newMarginTop = originalMarginTop + additionalHeight
      } else {
        // Collapse: restore original margin
        newMarginTop = originalMarginTop
      }

      // Apply the new margin-top
      mainContainer.style.marginTop = `${newMarginTop}px`
    }
  }, [headerHeight, isInModal, isTitleExpanded, originalMarginTop])

  const checkTextOverflow = useCallback(() => {
    if (titleRef.current) {
      const element = titleRef.current
      const isOverflowing = element.scrollWidth > element.clientWidth
      setShowExpandButton(isOverflowing)
    }
  }, [])

  useEffect(() => {
    measureHeaderHeight() // Initial measurement
    checkTextOverflow() // Check if text needs expansion

    const handleResize = () => {
      measureHeaderHeight()
      checkTextOverflow()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [measureHeaderHeight, checkTextOverflow, title])

  // Update main container margin when title expansion state changes
  useEffect(() => {
    // Use a small delay to allow CSS transitions to complete
    const timeoutId = setTimeout(() => {
      updateMainContainerMargin()
    }, 350) // Slightly longer than the CSS transition (0.3s)

    return () => clearTimeout(timeoutId)
  }, [isTitleExpanded, updateMainContainerMargin])

  return (
    <HeaderWrapper
      hasTestId={hasTestId}
      style={{
        minHeight: height || headerHeight,
      }}
      {...restProps}
    >
      <Affix
        className="fixed-header"
        style={{ position: isInModal ? 'absolute' : 'fixed', top: 0, right: 0 }}
      >
        <Container
          className={containerClassName}
          isInModal={isInModal}
          ref={headerRef}
          id="mainHeader"
          {...restProps}
        >
          <MenuIcon className="hamburger" onClick={() => toggleSideBar()} />
          {(isEditable || headingText) && (
            <HeaderLeftContainer
              className={headerLeftClassName}
              headingText={headingText}
              {...restProps}
              mt={titleMarginTop}
              data-cy="header-left-container"
            >
              {Icon && (
                <TitleIcon>
                  <Icon />
                </TitleIcon>
              )}
              {isEditable ? (
                <EditableTitleWrapper>
                  <EditableSizedWrapper
                    length={title?.length || 0}
                    hasSections={restProps.hasSections}
                  >
                    <TextInputOnFocusStyled
                      showArrow
                      value={title}
                      data-cy="title"
                      onChange={(e) => onTitleChange(e.target.value)}
                      size="large"
                      placeholder="Untitled Test"
                      margin="0px"
                      fontSize="18px"
                      onFocus={(event) => {
                        event.target.select()
                        setShowPencil(false)
                      }}
                      onBlur={() => setShowPencil(true)}
                      style={{
                        padding: '6px 12px',
                        textOverflow: 'ellipsis',
                      }}
                      aria-label="Enter Test Title"
                    />
                  </EditableSizedWrapper>
                  {showPencil && <IconPencilEdit />}
                  {restProps.headingSubContent}
                </EditableTitleWrapper>
              ) : (
                <>
                  <TitleButtonWrapper>
                    {showExpandButton && (
                      <StyledButton
                        onClick={() => setIsTitleExpanded(!isTitleExpanded)}
                        aria-label={
                          isTitleExpanded ? 'Collapse title' : 'Expand title'
                        }
                        aria-expanded={isTitleExpanded}
                      >
                        {isTitleExpanded ? <IconMinusRounded /> : <IconPlus />}
                      </StyledButton>
                    )}
                    <TitleWrapper
                      {...restProps}
                      title={typeof title === 'string' ? title : null}
                      data-cy="title"
                      isTitleExpanded={isTitleExpanded}
                      ref={titleRef}
                    >
                      {headingText}
                    </TitleWrapper>
                    {showExpandButton && (
                      <ScreenReaderLiveRegion
                        aria-live="polite"
                        aria-atomic="true"
                      >
                        {isTitleExpanded
                          ? `Title expanded: ${headingText}`
                          : 'Title collapsed'}
                      </ScreenReaderLiveRegion>
                    )}
                  </TitleButtonWrapper>
                  {restProps.headingSubContent}
                </>
              )}
              {restProps.renderBanner}
            </HeaderLeftContainer>
          )}

          {children}
        </Container>
      </Affix>
    </HeaderWrapper>
  )
}

MainHeader.propTypes = {
  children: PropTypes.any.isRequired,
  toggleSideBar: PropTypes.func.isRequired,
  headingText: PropTypes.string.isRequired,
  Icon: PropTypes.any.isRequired,
  t: PropTypes.any.isRequired,
}

const enhance = compose(
  connect(null, {
    toggleSideBar: toggleSideBarAction,
  })
)

export default enhance(MainHeader)

const HeaderWrapper = styled.div`
  padding-top: ${(props) => props.height || props.theme.HeaderHeight?.md}px;
  .ant-dropdown {
    /* the div hiding this has z-index 999 */
    z-index: 1000;
  }
  .fixed-header {
    position: fixed;
    top: 0;
    right: 0;
    left: ${({ hideSideMenu }) => (hideSideMenu ? '0' : '70px')};
    z-index: 999;
  }
  .tabAlignment {
    & > div {
      flex: ${(props) => (props.hasTestId ? '' : 1)};
    }
  }
  @media (max-width: ${mobileWidthMax}) {
    .fixed-header {
      left: 0;
    }
  }

  @media (min-width: ${mediumDesktopExactWidth}) {
    padding-top: ${(props) => props.height || props.theme.HeaderHeight?.md}px;
  }
  @media (min-width: ${extraDesktopWidthMax}) {
    padding-top: ${(props) => props.height || props.theme.HeaderHeight?.xl}px;
  }
  @media (max-width: ${smallDesktopWidth}) {
    padding-top: ${(props) => props.theme.HeaderHeight?.sd}px;
  }
  @media (max-width: ${mobileWidthLarge}) {
    height: ${(props) => props.theme.HeaderHeight?.xs}px;
    flex-wrap: wrap;
  }
  @media print {
    padding-top: 0px;
  }
`

const Container = styled.div`
  padding: 0px 30px;
  background: ${(props) => props.theme.header?.headerBgColor};
  display: ${(props) => props.display || 'flex'};
  justify-content: ${(props) => props.justify || 'space-between'};
  align-items: ${(props) => props.align || 'center'};
  border-bottom: 1px solid #2f4151;
  min-height: ${(props) => props.height || props.theme.HeaderHeight?.md}px;
  border-radius: ${({ isInModal }) => (isInModal ? '10px 10px 0px 0px' : '')};

  @media (min-width: ${extraDesktopWidthMax}) {
    min-height: ${(props) => props.height || props.theme.HeaderHeight?.xl}px;
  }
  @media (min-width: ${mediumDesktopExactWidth}) {
    min-height: ${(props) => props.height || props.theme.HeaderHeight?.md}px;
  }
  @media (max-width: ${smallDesktopWidth}) {
    min-height: ${(props) =>
      props.smallDesktopHeaderHeight || props.theme.HeaderHeight?.sd}px;
    flex-direction: ${(props) => props.smallDesktopFlexDirection || 'column'};
    align-items: ${(props) => props.align || 'normal'};
  }
  @media (max-width: ${mobileWidthLarge}) {
    min-height: ${(props) =>
      props.mobileHeaderHeight || props.theme.HeaderHeight?.xs}px;
    flex-wrap: wrap;
  }
`

export const HeaderLeftContainer = styled.div`
  display: ${(props) => props.display || 'flex'};
  align-items: ${(props) => props.alignItems || 'flex-start'};
  &:not(.headerLeftWrapper) {
    justify-content: ${(props) => props.justifyContent || 'space-evenly'};
  }
  flex-direction: ${(props) => props.flexDirection || 'row'};
  flex-wrap: ${(props) => props.flexWrap || ''};
  width: ${(props) => props.width || 'auto'};
  margin-top: ${({ mt }) => mt};

  @media (max-width: ${tabletWidth}) {
    margin-left: 8px;
    display: ${(props) => (!props.headingText ? 'none' : '')};
  }
`

const TitleButtonWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;

  button {
    margin-top: 10px;
  }

  span {
    margin-top: 10px;
  }
`

const ScreenReaderLiveRegion = styled.div`
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
  margin: -1px;
  padding: 0;
`

export const TitleWrapper = styled.h1`
  font-size: 18px;
  color: ${(props) => props.theme.header?.headerTitleTextColor};
  font-weight: bold;
  line-height: normal;
  min-width: auto;
  margin: 0px;
  text-wrap: ${(props) => (props.isTitleExpanded ? 'wrap' : 'nowrap')};
  padding-top: 5px;
  max-width: ${(props) =>
    props.noEllipsis ? 'unset' : props.titleMaxWidth || '352px'};
  overflow: hidden;
  letter-spacing: 0.9px;
  text-overflow: ${(props) => (props.isTitleExpanded ? 'visible' : 'ellipsis')};
  white-space: ${(props) => (props.isTitleExpanded ? 'normal' : 'nowrap')};
  transition: all 0.3s ease-in-out;
  max-height: ${(props) => (props.isTitleExpanded ? 'none' : '2.5em')};
  line-height: 1.2;
  overflow: ${(props) => (props.isTitleExpanded ? 'visible' : 'hidden')};

  @media (min-width: ${mediumDesktopExactWidth}) {
    font-size: ${(props) => props.theme.header?.headerTitleFontSize};
    min-width: ${(props) => props.titleMinWidth || '200px'};
  }
  @media (max-width: ${smallDesktopWidth}) {
    font-size: 14px;
    max-width: 14rem;
  }
  @media (max-width: ${middleMobileWidth}) {
    max-width: 14rem;
    margin-left: 1rem;
  }
  @media (max-width: ${smallMobileWidth}) {
    max-width: 7rem;
  }
  ${(props) =>
    props.hasSections &&
    css`
      min-width: auto !important;
      max-width: 16rem;
      @media (max-width: ${extraDesktopWidthMax}) {
        max-width: 8rem;
      }
      @media (max-width: ${extraDesktopWidth}) {
        max-width: 4rem;
      }
      @media (max-width: ${smallDesktopWidth}) {
        max-width: 8rem;
      }
    `}
`

const TitleIcon = styled.span`
  display: flex;
  align-items: center;
  margin-right: 15px;
  margin-top: 10px !important;
  svg {
    fill: ${(props) => props.theme.header?.headerTitleTextColor};
  }

  @media (max-width: ${smallDesktopWidth}) {
    svg {
      width: 16px;
      height: 16px;
    }
  }

  @media (max-width: ${tabletWidth}) {
    display: none;
  }
`

export const HeaderMidContainer = styled.div`
  align-self: flex-end;
  @media (max-width: ${mobileWidthMax}) {
    display: ${({ $display }) => $display || 'none'};
  }
`

const EditableTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  fontsize: 18px;
`

const EditableSizedWrapper = styled.div`
  width: calc(${(props) => (props.length || 0) + 1}ch + 32px);
  min-width: calc(${'Untitled Test'.length}ch + 32px);
  max-width: 300px;
  margin-right: 2px;
  box-sizing: border-box;
  @media (max-width: ${extraDesktopWidthMax}) {
    max-width: 14rem;
  }
  @media (max-width: ${largeDesktopWidth}) {
    min-width: calc(${'Untitled'.length}ch + 32px);
    font-size: 14px;
    max-width: 8rem;
  }
  @media (max-width: ${smallMobileWidth}) {
    min-width: calc(${'Unti'.length}ch + 32px);
    max-width: 5rem;
  }
  ${(props) =>
    props.hasSections &&
    css`
      max-width: 15rem;
      @media (max-width: ${extraDesktopWidthMax}) {
        min-width: calc(${'Untitled'.length}ch + 32px);
        max-width: 7rem;
      }
      @media (max-width: ${extraDesktopWidth}) {
        min-width: calc(${'Unti'.length}ch + 32px);
        max-width: 3rem;
      }
      @media (max-width: ${smallDesktopWidth}) {
        max-width: 7rem;
      }
    `}
`

const StyledButton = styled.button`
  background: none;
  border: 2px solid ${themeColor};
  border-radius: 50%;
  color: ${themeColor};
  cursor: pointer;
  padding: 0;
  margin: 0;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  margin-right: 10px;

  &:hover {
    border-color: ${themeColor};
    color: ${themeColor};
    svg {
      fill: ${themeColor};
    }
  }

  &:focus {
    outline: 2px solid ${accessibilityColors.theme.blue};
    outline-offset: 2px;
  }

  svg {
    width: 10px !important;
    height: 10px !important;
    fill: ${themeColor};
  }
`
